/**
 * Authentication Service
 *
 * Singleton service for managing authentication state and operations using MSAL Browser.
 * Handles login, logout, token acquisition, and authentication status.
 */

import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './config';

class AuthService {
	private msalInstance: PublicClientApplication;
	private initialized = false;

	constructor() {
		this.msalInstance = new PublicClientApplication(msalConfig);
	}

	/**
	 * Initialize MSAL and handle redirect response
	 * Call this once on app startup
	 */
	async initialize(): Promise<void> {
		if (this.initialized) {
			return;
		}

		await this.msalInstance.initialize();

		const response = await this.msalInstance.handleRedirectPromise();

		const accounts = this.msalInstance.getAllAccounts();

		this.initialized = true;
	}

	/**
	 * Initiate login flow (redirect-based)
	 */
	async login(): Promise<void> {
		try {
			await this.msalInstance.loginRedirect(loginRequest);
		} catch (error) {
			console.error('Login failed:', error);
			throw error;
		}
	}

	/**
	 * Logout and redirect
	 */
	async logout(): Promise<void> {
		await this.msalInstance.logoutRedirect();
	}

	/**
	 * Get access token for API calls
	 * Attempts silent acquisition, falls back to interactive if needed
	 */
	async getAccessToken(): Promise<string | null> {
		const accounts = this.msalInstance.getAllAccounts();
		if (accounts.length === 0) return null;

		try {
			const response = await this.msalInstance.acquireTokenSilent({
				...loginRequest,
				account: accounts[0],
			});
			return response.accessToken;
		} catch (error) {
			console.error('Silent token acquisition failed:', error);
			// Only redirect when user interaction is explicitly required (e.g. consent, MFA)
			// Do NOT redirect on transient errors like monitor_window_timeout
			if (error instanceof InteractionRequiredAuthError) {
				await this.msalInstance.acquireTokenRedirect(loginRequest);
			}
			return null;
		}
	}

	/**
	 * Force-refresh access token by bypassing the MSAL token cache.
	 * Used by the 401 response interceptor to recover from a stale token without
	 * any interactive fallback. Returns null on failure so the caller can decide
	 * whether to redirect to login.
	 */
	async forceRefreshAccessToken(): Promise<string | null> {
		const accounts = this.msalInstance.getAllAccounts();
		if (accounts.length === 0) return null;

		try {
			const response = await this.msalInstance.acquireTokenSilent({
				...loginRequest,
				account: accounts[0],
				forceRefresh: true,
			});
			return response.accessToken;
		} catch (error) {
			console.error('Forced silent token refresh failed:', error);
			return null;
		}
	}

	/**
	 * Re-enter our own login flow without bouncing through Microsoft's logout page.
	 * Clears the local MSAL cache so MSAL no longer considers the user signed in,
	 * then triggers loginRedirect. If the Microsoft session cookie is still valid
	 * (common for expired access tokens) the redirect completes silently via SSO.
	 */
	async signInAgain(): Promise<void> {
		try {
			await this.msalInstance.clearCache();
		} catch (error) {
			console.error('Failed to clear MSAL cache before re-login:', error);
		}
		await this.msalInstance.loginRedirect(loginRequest);
	}

	/**
	 * Check if user is authenticated
	 */
	async isAuthenticated(): Promise<boolean> {
		const accounts = this.msalInstance.getAllAccounts();
		return accounts.length > 0;
	}

	/**
	 * Get current user account info
	 */
	getCurrentUser(): AccountInfo | null {
		const accounts = this.msalInstance.getAllAccounts();
		return accounts[0] || null;
	}
}

// Export singleton instance
export const authService = new AuthService();
